import { useEffect, useRef, useState } from 'react';

interface RecaptchaCheckboxProps {
  /** reCAPTCHA v2 checkbox site key (different from score-based site key) */
  siteKey: string;
  /** The action name for this challenge (used as the callback identifier) */
  action: string;
  /** Called when the user completes the checkbox challenge */
  onVerified: (token: string) => void;
}

/**
 * RecaptchaCheckbox — Enterprise checkbox fallback for mid-score reCAPTCHA assessments.
 *
 * When the score-based reCAPTCHA returns a score between 0.3 and 0.5 for write
 * operations, the backend returns NEED_CHALLENGE (422). This component renders
 * a Google reCAPTCHA Enterprise checkbox for the user to complete manually.
 *
 * Uses enterprise.js (not api.js) so the generated token is compatible with
 * the Enterprise createAssessment API used on the backend.
 */
export default function RecaptchaCheckbox({ siteKey, action, onVerified }: RecaptchaCheckboxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const callbackName = `recaptchaCallback_${action}_${Date.now()}`;

    const renderWidget = () => {
      if (!containerRef.current || !window.grecaptcha?.enterprise?.render) return;
      (window as unknown as Record<string, (token: string) => void>)[callbackName] = (token: string) => {
        onVerified(token);
      };
      if (widgetIdRef.current !== null) {
        window.grecaptcha.enterprise.reset!(widgetIdRef.current);
      } else {
        widgetIdRef.current = window.grecaptcha.enterprise.render!(containerRef.current, {
          sitekey: siteKey,
          callback: callbackName,
          theme: 'light',
          size: 'normal',
        });
      }
      setLoading(false);
    };

    // Use enterprise.js (not api.js) so the token is compatible with the
    // Enterprise createAssessment API used on the backend.
    // If enterprise.js was already loaded by the score-based flow, grecaptcha.enterprise
    // will already be available — skip loading a second script.
    const scriptId = 'recaptcha-enterprise-checkbox-script';
    if (window.grecaptcha?.enterprise?.render) {
      // Already loaded (e.g. by score-based flow)
      renderWidget();
    } else if (!document.getElementById(scriptId)) {
      (window as unknown as Record<string, () => void>).recaptchaEnterpriseCheckboxOnLoad = renderWidget;
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://www.google.com/recaptcha/enterprise.js?onload=recaptchaEnterpriseCheckboxOnLoad&render=explicit`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } else {
      // Script loading, wait for onload
      (window as unknown as Record<string, () => void>).recaptchaEnterpriseCheckboxOnLoad = renderWidget;
    }

    return () => {
      delete (window as unknown as Record<string, (token: string) => void>)[callbackName];
    };
  }, [siteKey, action]); // eslint-disable-line react-hooks/exhaustive-deps
  // onVerified intentionally excluded — it's stored via window callback, not React closure

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      {loading && (
        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      )}
      <div ref={containerRef} />
      <p className="text-xs text-muted-foreground text-center">
        Please complete the security check to continue
      </p>
    </div>
  );
}
